"use client";

import * as React from "react";
import { Check } from "lucide-react";
import { cn } from "./cn";

// Lightweight checkbox styled to match Input/Select (mist→paper, lime accent).
// Renders a hidden native <input type="checkbox"> so react-hook-form's
// `register()` plugs straight in without a Controller wrapper, and the
// visual swap is driven by tailwind `peer-checked:` so it stays in sync
// with the DOM regardless of who owns the state.
export type CheckboxProps = Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  "type" | "size"
> & {
  label?: React.ReactNode;
  description?: React.ReactNode;
  invalid?: boolean;
};

export const Checkbox = React.forwardRef<HTMLInputElement, CheckboxProps>(
  ({ className, label, description, invalid, disabled, id, ...props }, ref) => {
    const reactId = React.useId();
    const inputId = id ?? reactId;
    return (
      <label
        htmlFor={inputId}
        className={cn(
          "inline-flex items-start gap-2.5 cursor-pointer select-none",
          disabled && "cursor-not-allowed opacity-60",
          className,
        )}
      >
        <span className="relative h-5 w-5 mt-0.5 shrink-0">
          <input
            ref={ref}
            id={inputId}
            type="checkbox"
            disabled={disabled}
            aria-invalid={invalid || undefined}
            className="peer absolute inset-0 h-5 w-5 cursor-pointer appearance-none m-0"
            {...props}
          />
          <span
            aria-hidden
            className={cn(
              "pointer-events-none absolute inset-0 rounded-[6px] border-[1.5px]",
              "bg-mist border-smoke transition-colors",
              "peer-checked:bg-lime peer-checked:border-lime",
              "peer-focus-visible:ring-2 peer-focus-visible:ring-lime peer-focus-visible:ring-offset-1",
              invalid &&
                "border-danger peer-checked:bg-danger peer-checked:border-danger",
            )}
          />
          <Check
            aria-hidden
            size={14}
            strokeWidth={3}
            className={cn(
              "pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2",
              "text-ink opacity-0 peer-checked:opacity-100 transition-opacity",
            )}
          />
        </span>
        {label || description ? (
          <span className="flex flex-col gap-0.5">
            {label ? (
              <span className="text-[14px] font-medium text-ink leading-tight">
                {label}
              </span>
            ) : null}
            {description ? (
              <span className="text-[12px] text-ash leading-snug">
                {description}
              </span>
            ) : null}
          </span>
        ) : null}
      </label>
    );
  },
);
Checkbox.displayName = "Checkbox";
